import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { parseContentDispositionFilename } from '../core/api-response-utils';
import type { Pageable } from './service-common';
import type {
  ListPaymentFilterSpec,
  WrapperPageableResponsePaymentDTO,
  WrapperResponsePaymentDTO
} from '../core/models';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  listPayments(
    spec: ListPaymentFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponsePaymentDTO> {
    const params = this.buildListParams(spec, pageable);
    return this.http.get<WrapperPageableResponsePaymentDTO>(`${this.apiUrl}/payments/list`, { params });
  }

  exportPaymentsCsv(
    spec: ListPaymentFilterSpec = {},
    sort: string[] = ['paidAt,desc']
  ): Observable<{ blob: Blob; filename: string }> {
    let params = this.buildSpecParams(spec);
    sort.forEach((s) => {
      params = params.append('sort', s);
    });
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      params = params.append('timeZone', timeZone);
    }

    return this.http
      .get(`${this.apiUrl}/payments/export`, {
        params,
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map((resp) => ({
          blob: resp.body as Blob,
          filename: parseContentDispositionFilename(resp.headers.get('Content-Disposition')) ?? 'pagos.csv'
        }))
      );
  }

  getPaymentById(id: number): Observable<WrapperResponsePaymentDTO> {
    return this.http.get<WrapperResponsePaymentDTO>(`${this.apiUrl}/payments/${id}`);
  }

  private buildListParams(spec: ListPaymentFilterSpec, pageable: Pageable): HttpParams {
    let params = this.buildSpecParams(spec);
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));
    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach((sort) => {
        params = params.append('sort', sort);
      });
    }
    return params;
  }

  private buildSpecParams(spec: ListPaymentFilterSpec): HttpParams {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    return params;
  }
}
